import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { request, useOperations } from './OperationsProvider';
import { formatDateTime } from '../common/util/datetime';
import { exportCsv, parseCsv } from './csv';
import { latestAssignment } from './devices';
import { downloadRegistry } from './registryExport';

const KINDS = [['GPS', 'GPS'], ['RFID', 'RFID'], ['EXTERNAL', 'External feed']];
const STATUSES = [['ACTIVE', 'Active'], ['FAULTY', 'Faulty'], ['LOST', 'Lost'], ['RETIRED', 'Retired']];
const statusOf = (device) => device.status || 'ACTIVE';
const statusLabel = (device) => STATUSES.find(([value]) => value === statusOf(device))?.[1] || statusOf(device);
const kindLabel = (kind) => KINDS.find(([value]) => value === kind)?.[1] || kind;
// A GPS row no longer carries a tracking reference for the common case: the identifier is matched
// against the devices the platform has made available to this workspace. The column stays, under a
// name that says what it is actually for - a device on the workspace's own VTS - and the old header
// is still accepted so files written before this keep importing.
const TEMPLATE_COLUMNS = ['identifier', 'type', 'status', 'site', 'sim_number', 'model', 'vts_device_id'];
const COLUMN_KEYS = {
  identifier: 'identifier', type: 'kind', kind: 'kind', status: 'status', site: 'site', simnumber: 'simNumber', sim: 'simNumber', model: 'model', trackingreference: 'providerId', providerid: 'providerId', vtsdeviceid: 'providerId',
};

// What the device is doing now, read from its latest assignment.
const currentUse = (device, assignments, vehicles) => {
  const latest = latestAssignment(device.id, assignments);
  const vehicle = (id) => vehicles.find((v) => v.id === id)?.name || 'a vehicle';
  if (latest && !latest.endedAt) return { label: `On ${vehicle(latest.vehicleId)} · ${latest.mode === 'PERMANENT' ? 'Permanent' : 'Temporary'}`, color: 'success' };
  if (latest?.mode === 'TEMPORARY' && !latest.returnedAt && !['LOST', 'RETIRED'].includes(statusOf(device))) return { label: `Awaiting return from ${vehicle(latest.vehicleId)}`, color: 'warning' };
  if (statusOf(device) !== 'ACTIVE') return { label: 'Cannot be issued', color: 'default' };
  return { label: 'Available', color: 'default' };
};

const EditDevice = ({ device, onClose }) => {
  const { data, mutate } = useOperations();
  const assigned = data.assignments.some((a) => a.sourceId === device.id);
  const [form, setForm] = useState({
    identifier: device.identifier, kind: device.kind, status: statusOf(device), site: device.site || '', simNumber: device.simNumber || '', model: device.model || '', providerId: device.providerId ?? '',
  });
  const [pending, setPending] = useState(false); const [error, setError] = useState(null);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const save = async (event) => {
    event.preventDefault(); setPending(true); setError(null);
    try { await mutate(`sources/${device.id}`, form, 'PATCH'); onClose(); } catch (e) { setError(e.message); } finally { setPending(false); }
  };
  return (
    <Dialog open onClose={() => !pending && onClose()} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={save}>
        <DialogTitle>{`Edit ${device.identifier}`}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField required label="Device identifier" value={form.identifier} onChange={set('identifier')} inputProps={{ maxLength: 100 }} helperText="Correct a mistyped ID here. Assignment history stays with the device." />
            <TextField select required label="Type" value={form.kind} onChange={set('kind')} disabled={assigned} helperText={assigned ? 'The type is fixed once the device has been assigned.' : undefined}>
              {KINDS.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
            </TextField>
            <TextField select required label="Status" value={form.status} onChange={set('status')} helperText="Faulty, lost and retired devices cannot be issued. Detach a device before retiring it.">
              {STATUSES.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
            </TextField>
            <TextField label="Site (optional)" value={form.site} onChange={set('site')} inputProps={{ maxLength: 100 }} />
            <TextField label="SIM number (optional)" value={form.simNumber} onChange={set('simNumber')} inputProps={{ maxLength: 25 }} />
            <TextField label="Model (optional)" value={form.model} onChange={set('model')} inputProps={{ maxLength: 100 }} />
            {form.kind === 'GPS' && <TextField label="Tracking reference (optional)" value={form.providerId} onChange={set('providerId')} helperText="The device ID in the connected tracking service." />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={pending}>{pending ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const DeviceHistory = ({ device, onClose }) => {
  const { data } = useOperations();
  const [activity, setActivity] = useState(null); const [error, setError] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    request(`/api/operations/sources/${device.id}/activity`, 'GET', null, controller.signal).then(setActivity).catch((e) => { if (e.name !== 'AbortError') setError(e.message); });
    return () => controller.abort();
  }, [device.id]);
  const vehicle = (id) => data.vehicles.find((v) => v.id === id)?.name || `Vehicle ${id}`;
  const rows = data.assignments.filter((a) => a.sourceId === device.id).sort((a, b) => b.id - a.id);
  const returned = (a) => {
    if (a.mode !== 'TEMPORARY') return '—';
    if (a.returnedAt) return `${formatDateTime(a.returnedAt)}${a.returnInferred ? ' (inferred)' : ''}`;
    return a.endedAt ? 'Not yet' : '—';
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{`${device.identifier} · ${kindLabel(device.kind)} · ${statusLabel(device)}`}</DialogTitle>
      <DialogContent>
        <Stack gap={2.5}>
          {error && <Alert severity="warning">{error}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">Last GPS fix</Typography>
              <Typography>{activity ? (activity.lastFix ? formatDateTime(activity.lastFix.fixTime) : 'None received') : 'Loading…'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Last checkpoint read</Typography>
              <Typography>{activity ? (activity.lastDetection ? `${activity.lastDetection.checkpoint} · ${formatDateTime(activity.lastDetection.detectedAt)}` : 'None recorded') : 'Loading…'}</Typography>
            </Box>
          </Stack>
          <Typography variant="subtitle2">Assignment history</Typography>
          {rows.length ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>{['Vehicle', 'Mode', 'Attached', 'Released', 'Returned'].map((h) => <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{vehicle(a.vehicleId)}</TableCell>
                      <TableCell>{a.mode === 'PERMANENT' ? 'Permanent' : 'Temporary'}</TableCell>
                      <TableCell>{formatDateTime(a.startedAt)}</TableCell>
                      <TableCell>{a.endedAt ? formatDateTime(a.endedAt) : 'Still attached'}</TableCell>
                      <TableCell>{returned(a)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Typography color="text.secondary">This device has never been assigned.</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

const ImportDevices = ({ onClose }) => {
  const { refresh } = useOperations();
  const [rows, setRows] = useState(null); const [fileError, setFileError] = useState(null);
  const [result, setResult] = useState(null); const [pending, setPending] = useState(false);
  const read = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setRows(null); setResult(null); setFileError(null);
    file.text().then((text) => {
      const [header = [], ...lines] = parseCsv(text);
      const keys = header.map((name) => COLUMN_KEYS[name.toLowerCase().replace(/[^a-z]/g, '')]);
      if (!keys.includes('identifier') || !keys.includes('kind')) {
        setFileError('The first row must name the columns, including identifier and type. Download the template to start from it.');
        return;
      }
      setRows(lines
        .map((cells, index) => ({ cells, line: index + 2 }))
        .filter(({ cells }) => cells.some((cell) => cell.trim()))
        .map(({ cells, line }) => {
          const row = { line };
          keys.forEach((key, index) => { if (key) row[key] = (cells[index] || '').trim(); });
          return row;
        }));
    }).catch(() => setFileError('The file could not be read.'));
  };
  const submit = async () => {
    setPending(true); setResult(null);
    try {
      const response = await request('/api/operations/sources/import', 'POST', { rows });
      setResult(response);
      if (response.created) await refresh();
    } catch (e) { setResult({ created: 0, errors: [{ row: '—', message: e.message }] }); } finally { setPending(false); }
  };
  const template = () => exportCsv('device-import-template', TEMPLATE_COLUMNS, [
    {
      identifier: 'GPS-0001', type: 'GPS', status: 'ACTIVE', site: 'North plant', sim_number: '8991000000000000001', model: 'Portable tracker', vts_device_id: '',
    },
    {
      identifier: 'TAG-0001', type: 'RFID', status: 'ACTIVE', site: 'North plant', sim_number: '', model: 'UHF windscreen tag', vts_device_id: '',
    },
  ]);
  const done = result?.created > 0;
  return (
    <Dialog open onClose={() => !pending && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Import devices</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">Columns: identifier and type (GPS, RFID or EXTERNAL), plus optional status, site, SIM number and model. A GPS identifier must be one the platform has made available to you; use the VTS device id column only for a device on your own VTS. Nothing is imported if any row has a problem.</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button onClick={template}>Download template</Button>
            <Button component="label" variant="outlined" disabled={pending}>
              Choose CSV file
              <input type="file" hidden accept=".csv,text/csv" onChange={read} />
            </Button>
          </Stack>
          {fileError && <Alert severity="error">{fileError}</Alert>}
          {rows && !done && <Typography>{`${rows.length} device ${rows.length === 1 ? 'row' : 'rows'} ready to import.`}</Typography>}
          {done && <Alert severity="success">{`${result.created} devices registered.`}</Alert>}
          {result?.errors?.length > 0 && (
            <Alert severity="error">
              {`Nothing was imported. Fix ${result.errors.length === 1 ? 'this row' : `these ${result.errors.length} rows`} and import the file again:`}
              <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 200, overflow: 'auto' }}>
                {result.errors.map((e) => <li key={`${e.row}-${e.message}`}>{`Row ${e.row}: ${e.message}`}</li>)}
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={onClose}>{done ? 'Done' : 'Cancel'}</Button>
        {!done && <Button variant="contained" disabled={pending || !rows?.length} onClick={submit}>{pending ? 'Importing…' : 'Import'}</Button>}
      </DialogActions>
    </Dialog>
  );
};

// Reads from tags or devices nobody has registered. Seeing them here is how a new windscreen tag or
// a mistyped registration gets noticed; their earlier reads are not attributed after registering.
const UnregisteredReads = ({ administer, onRegister }) => {
  const { data } = useOperations();
  const [state, setState] = useState({ items: [], error: null, loaded: false });
  useEffect(() => {
    const controller = new AbortController();
    request('/api/operations/unregistered-reads', 'GET', null, controller.signal)
      .then((items) => setState({ items, error: null, loaded: true }))
      .catch((e) => { if (e.name !== 'AbortError') setState({ items: [], error: e.message, loaded: true }); });
    return () => controller.abort();
  }, [data.sources]);
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="h6">Unregistered reads</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Tags and devices that readers or feeds reported but that are not in the register.</Typography>
      {state.error && <Alert severity="warning">{state.error}</Alert>}
      {state.loaded && !state.error && !state.items.length && <Typography color="text.secondary">No unregistered reads.</Typography>}
      {state.items.map((read) => (
        <Stack key={`${read.kind}-${read.identifier}`} direction="row" gap={2} alignItems="center" sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>{`${read.identifier} · ${kindLabel(read.kind)}`}</Typography>
            <Typography variant="caption" color="text.secondary">{`${read.reads} ${read.reads === 1 ? 'read' : 'reads'} · last ${formatDateTime(read.lastSeenAt)}${read.checkpoint ? ` at ${read.checkpoint}` : ''}`}</Typography>
          </Box>
          {administer && <Button size="small" onClick={() => onRegister(read)}>Register</Button>}
        </Stack>
      ))}
    </Paper>
  );
};

// The device register: every RFID tag, GPS unit and external feed, its status and what it is doing
// now. Faulty, lost and retired devices stay listed with their history rather than being deleted.
const DeviceTable = ({
  rows: all, administer, operate, onRegister, reporting = '', onClearReporting,
}) => {
  const { data, refresh } = useOperations();
  const [kind, setKind] = useState(''); const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null); const [viewing, setViewing] = useState(null); const [importing, setImporting] = useState(false);
  // Removing a device is an administrator's decision and is confirmed in front of them, because a
  // unit that has been in service is retired rather than erased and the two are not the same thing.
  const [removing, setRemoving] = useState(null); const [removeError, setRemoveError] = useState(''); const [removePending, setRemovePending] = useState(false);
  const held = (device) => (data.assignments || []).filter((a) => a.sourceId === device.id);
  const remove = async () => {
    setRemovePending(true); setRemoveError('');
    try {
      await request(`/api/operations/sources/${removing.id}`, 'DELETE');
      await refresh();
      setRemoving(null);
    } catch (e) { setRemoveError(e.message); } finally { setRemovePending(false); }
  };
  // A tracker that is out on a truck and has not reported inside the configured interval - the
  // same reading the briefing counts, so the sentence and this list cannot disagree.
  const interval = data.config?.reportingIntervalMs || 0;
  const silent = (device) => {
    const held = (data.assignments || []).find((a) => a.sourceId === device.id && !a.endedAt && a.kind === 'GPS');
    if (!held) return false;
    return !device.lastFixAt || Date.now() - Date.parse(device.lastFixAt) > interval;
  };
  const rows = reporting === 'quiet' && interval > 0 ? all.filter(silent) : all;
  const filtered = rows.filter((device) => (!kind || device.kind === kind) && (!status || statusOf(device) === status));
  return (
    <Stack gap={3}>
      {reporting === 'quiet' && (
        <Alert
          severity="warning"
          action={onClearReporting && <Button size="small" onClick={onClearReporting}>Show all devices</Button>}
        >
          {`Showing the ${rows.length} issued ${rows.length === 1 ? 'tracker that has' : 'trackers that have'} stopped reporting.`}
        </Alert>
      )}
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <TextField select label="Type" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All types</MenuItem>
          {KINDS.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
        </TextField>
        <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All statuses</MenuItem>
          {STATUSES.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">{`${filtered.length} of ${rows.length} devices`}</Typography>
        {administer && <Button variant="outlined" sx={{ ml: 'auto' }} onClick={() => setImporting(true)}>Import CSV</Button>}
        {/* The devices as the table shows them, type and status filters included - not the whole
            register under a filtered heading. */}
        <Button variant="outlined" sx={{ ml: administer ? 0 : 'auto' }} onClick={() => downloadRegistry('sources', filtered, data)}>Download CSV</Button>
      </Stack>
      {filtered.length === 0 ? <Typography color="text.secondary" sx={{ p: 3 }}>No devices match this search.</Typography> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Device', 'Type', 'Status', 'Site', 'SIM', 'Current use', ''].map((h) => <TableCell key={h || 'actions'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((device) => {
                const use = currentUse(device, data.assignments, data.vehicles);
                const state = statusOf(device);
                return (
                  <TableRow key={device.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{device.identifier}</Typography>
                      {(device.model || device.providerId) && <Typography variant="caption" color="text.secondary">{[device.model, device.providerId && `Tracking reference ${device.providerId}`].filter(Boolean).join(' · ')}</Typography>}
                    </TableCell>
                    <TableCell>{kindLabel(device.kind)}</TableCell>
                    <TableCell><Chip size="small" label={statusLabel(device)} color={state === 'FAULTY' || state === 'LOST' ? 'error' : 'default'} variant={state === 'ACTIVE' ? 'outlined' : 'filled'} /></TableCell>
                    <TableCell>{device.site || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{device.simNumber || '—'}</TableCell>
                    <TableCell><Chip size="small" label={use.label} color={use.color} variant="outlined" /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" onClick={() => setViewing(device)}>History</Button>
                      {administer && <Button size="small" onClick={() => setEditing(device)}>Edit</Button>}
                      {administer && <Button size="small" color="error" onClick={() => { setRemoving(device); setRemoveError(''); }}>Remove</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {operate && <UnregisteredReads administer={administer} onRegister={onRegister} />}
      <Dialog open={!!removing} onClose={() => !removePending && setRemoving(null)} fullWidth maxWidth="xs">
        <DialogTitle>{removing && held(removing).length ? `Retire ${removing.identifier}?` : `Remove ${removing?.identifier}?`}</DialogTitle>
        <DialogContent>
          {removeError && <Alert severity="error" sx={{ mb: 2 }}>{removeError}</Alert>}
          <Typography>
            {removing && held(removing).length
              ? `This device has been issued ${held(removing).length === 1 ? 'once' : `${held(removing).length} times`}. Trips and recorded positions name it, so it is retired and taken out of circulation rather than deleted.`
              : 'This device has never been issued, so it is deleted from the register.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={removePending} onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={removePending} onClick={remove}>
            {removePending ? 'Working…' : removing && held(removing).length ? 'Retire' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      {editing && <EditDevice device={editing} onClose={() => setEditing(null)} />}
      {viewing && <DeviceHistory device={viewing} onClose={() => setViewing(null)} />}
      {importing && <ImportDevices onClose={() => setImporting(false)} />}
    </Stack>
  );
};

export default DeviceTable;
